import React, { useState, useEffect, useCallback } from "react";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  Badge,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import SimpleBar from "simplebar-react";
import {
  getNotificacionesNoLeidas,
  marcarNotificacionLeida,
  marcarTodasNotificacionesLeidas,
} from "../../helpers/api_helper";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

// Configuración visual por tipo de notificación
const TIPO_CONFIG = {
  ticket: {
    icon: 'ri-headphone-line',
    bgClass: 'bg-warning-subtle',
    textClass: 'text-warning',
    label: 'Ticket',
    badgeColor: 'warning',
  },
  comentario: {
    icon: 'ri-message-3-line',
    bgClass: 'bg-info-subtle',
    textClass: 'text-info',
    label: 'Comentario',
    badgeColor: 'info',
  },
  default: {
    icon: 'ri-notification-3-line',
    bgClass: 'bg-primary-subtle',
    textClass: 'text-primary',
    label: 'Notificación',
    badgeColor: 'primary',
  }
};

const getNotifConfig = (tipo) => TIPO_CONFIG[tipo] || TIPO_CONFIG.default;

const NotificationDropdown = () => {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState([]);
  const [cantidadNoLeidas, setCantidadNoLeidas] = useState(0);
  const [isNotificationDropdown, setIsNotificationDropdown] = useState(false);

  // Función para obtener las notificaciones
  const obtenerNotificaciones = useCallback(async () => {
    try {
      const response = await getNotificacionesNoLeidas();
      if (response.status === 200) {
        setNotificaciones(response.data.data.notificaciones || []);
        setCantidadNoLeidas(response.data.data.cantidad || 0);
      }
    } catch (error) {
      console.error("Error al obtener notificaciones:", error);
    }
  }, []);

  // Cargar notificaciones al montar el componente
  useEffect(() => {
    obtenerNotificaciones();
    const intervalo = setInterval(obtenerNotificaciones, 300000);
    return () => clearInterval(intervalo);
  }, [obtenerNotificaciones]);

  // Función para marcar como leída
  const handleMarcarLeida = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await marcarNotificacionLeida(id);
      obtenerNotificaciones();
    } catch (error) {
      console.error("Error al marcar notificación como leída:", error);
    }
  };

  // Función para marcar todas las notificaciones como leídas
  const handleMarcarTodasLeidas = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await marcarTodasNotificacionesLeidas();
      obtenerNotificaciones();
    } catch (error) {
      console.error("Error al marcar todas las notificaciones como leídas:", error);
    }
  };

  // Formatear fecha relativa
  const formatearFecha = (fecha) => {
    try {
      return formatDistanceToNow(new Date(fecha), {
        addSuffix: true,
        locale: es,
      });
    } catch (error) {
      return "fecha desconocida";
    }
  };

  // Función para navegar según el tipo de notificación
  const navegarANotificacion = (notificacion) => {
    const { referencia_id, id: notificacionId, tipo } = notificacion;
    marcarNotificacionLeida(notificacionId)
      .then(() => {
        if (tipo === 'ticket') {
          navigate('/soporte/tickets');
        } else {
          navigate(`/solicitud/ver/${referencia_id}`);
        }
      })
      .catch((error) => {
        console.error("Error al marcar notificación:", error);
        if (tipo === 'ticket') {
          navigate('/soporte/tickets');
        } else {
          navigate(`/solicitud/ver/${referencia_id}`);
        }
      });
  };

  // Toggle dropdown
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdown(!isNotificationDropdown);
  };

  return (
    <React.Fragment>
      <Dropdown
        isOpen={isNotificationDropdown}
        toggle={toggleNotificationDropdown}
        className="topbar-head-dropdown ms-1 header-item"
      >
        <DropdownToggle
          type="button"
          tag="button"
          className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle"
        >
          <i className="bx bx-bell fs-22"></i>
          {cantidadNoLeidas > 0 && (
            <span className="position-absolute topbar-badge fs-10 translate-middle badge rounded-pill bg-danger">
              {cantidadNoLeidas}
              <span className="visually-hidden">mensajes no leídos</span>
            </span>
          )}
        </DropdownToggle>

        <DropdownMenu className="dropdown-menu-lg dropdown-menu-end p-0" style={{ minWidth: '380px' }}>
          {/* Header */}
          <div className="dropdown-head bg-primary bg-pattern rounded-top">
            <div className="p-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="m-0 fs-16 fw-semibold text-white">
                    <i className="ri-notification-3-line me-2 align-middle"></i>
                    Notificaciones
                  </h6>
                </div>
                {cantidadNoLeidas > 0 && (
                  <span className="badge rounded-pill fs-12" style={{ backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>
                    {cantidadNoLeidas} {cantidadNoLeidas === 1 ? 'nueva' : 'nuevas'}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Barra de acciones */}
          {notificaciones.length > 0 && (
            <div className="px-3 py-2 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
              <div className="d-flex align-items-center justify-content-between">
                <span className="text-muted fs-12 fw-medium text-uppercase" style={{ letterSpacing: '0.5px' }}>
                  Actividad reciente
                </span>
                <button
                  className="btn btn-link btn-sm text-decoration-none p-0 fs-12 fw-medium"
                  onClick={handleMarcarTodasLeidas}
                  style={{ color: '#405189' }}
                >
                  <i className="ri-check-double-line me-1 align-middle"></i>
                  Marcar todas como leídas
                </button>
              </div>
            </div>
          )}

          {/* Lista de notificaciones */}
          <SimpleBar style={{ maxHeight: "360px" }}>
            {notificaciones.length > 0 ? (
              <div className="py-1">
                {notificaciones.map((notificacion, index) => {
                  const config = getNotifConfig(notificacion.tipo);
                  return (
                    <div
                      key={notificacion.id || index}
                      className="notification-item-wrapper"
                      onClick={() => navegarANotificacion(notificacion)}
                      style={{
                        cursor: 'pointer',
                        padding: '12px 16px',
                        borderBottom: index < notificaciones.length - 1 ? '1px solid #f0f0f0' : 'none',
                        transition: 'background-color 0.15s ease',
                        backgroundColor: 'transparent',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <div className="d-flex align-items-start gap-3">
                        {/* Ícono */}
                        <div
                          className={`d-flex align-items-center justify-content-center rounded-circle flex-shrink-0 ${config.bgClass} ${config.textClass}`}
                          style={{ width: '38px', height: '38px', fontSize: '16px' }}
                        >
                          <i className={config.icon}></i>
                        </div>

                        {/* Contenido */}
                        <div className="flex-1" style={{ minWidth: 0 }}>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <Badge color={config.badgeColor} className="fs-10 fw-medium" pill>
                              {config.label}
                            </Badge>
                          </div>
                          <p className="mb-1 fs-13 text-dark fw-medium" style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: '1.4',
                          }}>
                            {notificacion.mensaje}
                          </p>
                          <div className="d-flex align-items-center justify-content-between">
                            <span className="text-muted fs-11">
                              <i className="ri-time-line me-1 align-middle"></i>
                              {formatearFecha(notificacion.fecha_creacion)}
                            </span>
                            <button
                              className="btn btn-link btn-sm text-decoration-none p-0 fs-11"
                              onClick={(e) => handleMarcarLeida(notificacion.id, e)}
                              style={{ color: '#878a99' }}
                              onMouseEnter={(e) => e.target.style.color = '#405189'}
                              onMouseLeave={(e) => e.target.style.color = '#878a99'}
                            >
                              <i className="ri-check-line me-1 align-middle"></i>
                              Leída
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-5 px-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle bg-light mx-auto mb-3"
                  style={{ width: '64px', height: '64px' }}
                >
                  <i className="ri-notification-off-line fs-28 text-muted"></i>
                </div>
                <h6 className="fs-14 fw-semibold text-dark mb-1">
                  Sin notificaciones
                </h6>
                <p className="text-muted fs-12 mb-0">
                  No tienes notificaciones pendientes por leer
                </p>
              </div>
            )}
          </SimpleBar>

          {/* Footer */}
          {notificaciones.length > 0 && (
            <div className="border-top text-center py-2" style={{ backgroundColor: '#f8f9fa' }}>
              <span className="text-muted fs-11">
                Mostrando {notificaciones.length} {notificaciones.length === 1 ? 'notificación' : 'notificaciones'} sin leer
              </span>
            </div>
          )}
        </DropdownMenu>
      </Dropdown>
    </React.Fragment>
  );
};

export default NotificationDropdown;
